/**
 * Custom Accordion Web Component
 * Handles accordion functionality with smooth height transitions
 */
class CustomAccordion extends HTMLElement {
  connectedCallback() {
    this.initializeAccordions();
    this.handleAnchors();
    this.setupHashChangeListener();
  }

  initializeAccordions() {
    const accordions = this.querySelectorAll('.accordion-container');

    accordions.forEach(accordion => {
      const button = accordion.querySelector('.accordion-button');
      const content = accordion.querySelector('.accordion-content');
      const icon = accordion.querySelector('.accordion-icon');

      if (accordion instanceof HTMLElement && button && content instanceof HTMLElement) {
        button.addEventListener('click', () => {
          const isExpanded = button.getAttribute('aria-expanded') === 'true';

          button.setAttribute('aria-expanded', String(!isExpanded));
          //make it slide open and close
          accordion.style.transition = 'height 0.3s ease';
          if (!isExpanded) {
            content.style.height = content.scrollHeight + 'px';
            content.setAttribute('aria-hidden', 'false');
            content.removeAttribute('inert');
            if (icon instanceof HTMLElement) icon.style.transform = 'rotate(-180deg)';
          } else {
            content.style.height = '0px';
            content.setAttribute('aria-hidden', 'true');
            content.setAttribute('inert', '');
            if (icon instanceof HTMLElement) icon.style.transform = 'rotate(0deg)';
          }
        });
      }
    });
  }

  handleAnchors() {
    // Check if there's a hash in the URL on page load
    if (window.location.hash) {
      this.openAccordionFromHash(window.location.hash);
    }
  }

  setupHashChangeListener() {
    // Listen for hash changes (e.g., when user clicks a link with anchor)
    window.addEventListener('hashchange', () => {
      if (window.location.hash) {
        this.openAccordionFromHash(window.location.hash);
      }
    });
  }

  /**
   * Opens an accordion based on the URL hash
   * @param {string} hash - The URL hash (including the # symbol)
   */
  openAccordionFromHash(hash) {
    // Remove the # from the hash
    const anchorId = hash.substring(1);
    
    // Check if it's an accordion anchor (starts with 'accordion-')
    if (anchorId.startsWith('accordion-')) {
      const targetAccordion = document.getElementById(anchorId);
      
      if (targetAccordion) {
        const button = targetAccordion.querySelector('.accordion-button');
        const content = targetAccordion.querySelector('.accordion-content');
        
        if (button && content instanceof HTMLElement) {
          // Open the accordion if it's not already open
          const isExpanded = button.getAttribute('aria-expanded') === 'true';
          
          content.style.transition = 'height 0.3s ease';
          if (!isExpanded) {
            button.setAttribute('aria-expanded', 'true');
            content.style.height = content.scrollHeight + 'px';
            content.setAttribute('aria-hidden', 'false');
            content.removeAttribute('inert');
          }
          
          // Scroll to the accordion with a small delay to ensure it's opened
          setTimeout(() => {
            targetAccordion.scrollIntoView({ 
              behavior: 'smooth', 
              block: 'start' 
            });
          }, 100);
        }
      }
    }
  }
}

if (!customElements.get('custom-accordion')) {
  customElements.define('custom-accordion', CustomAccordion);
}
